import { DownOutlined, PlusOutlined } from '@ant-design/icons';
import {
  FishingReport,
  SeasonGroup,
  Species,
  Technique,
  WaterbodyDetail,
} from '@omniafishing/core';
import { Dropdown } from 'antd';
import indefinite from 'indefinite';
import _ from 'lodash';
import React from 'react';
import { useResponsive } from '../../../../hooks/use_responsive';
import { WebAnalytics } from '../../../../lib/web_analytics';
import { KitInline } from '../../../../routes/get_started/kit_inline/kit_inline';
import { OmniaButton } from '../../../omnia_button/omnia_button';
import { useWaterbodyWebAnalyticsArgs } from '../../waterbody_detail_lib/use_waterbody_web_analytics_args';
import { TechniqueFullWithReportCount } from '../waterbody_detail_top_products_top_techniques/waterbody_detail_top_techniques';
import styles from './waterbody_detail_inline_kit.less';

interface WaterbodyDetailInlineKitProps {
  techniques: TechniqueFullWithReportCount[];
  selectedSeasonSpeciesFishingReports: FishingReport[];
  recentTrendingReports: FishingReport[];
  species: Species;
  waterbody: WaterbodyDetail;
  seasonGroup: SeasonGroup;
}

export const WaterbodyDetailInlineKit = (props: WaterbodyDetailInlineKitProps) => {
  const {
    techniques,
    selectedSeasonSpeciesFishingReports,
    species,
    waterbody,
    seasonGroup,
    recentTrendingReports,
  } = props;

  const fishingReports =
    recentTrendingReports.length && selectedSeasonSpeciesFishingReports.length
      ? _.uniqBy(
          [...selectedSeasonSpeciesFishingReports, ...recentTrendingReports].filter(Boolean),
          'id'
        )
      : [];

  const { isDesktop } = useResponsive();

  const webAnalyticsLakeClickArgs = useWaterbodyWebAnalyticsArgs(waterbody);
  const [isKitOpen, setIsKitOpen] = React.useState(false);
  const [selectedTechniques, setSelectedTechniques] = React.useState<Technique[]>([]);
  if (!selectedSeasonSpeciesFishingReports.length && !recentTrendingReports.length) {
    return null;
  }
  const selectedTechniquesNames = selectedTechniques.map((t) => t.name);
  const unselectedTechniques = techniques.filter(
    (t) => !selectedTechniquesNames.includes(t.name) && t.reportCount > 0
  );

  return (
    <div className={styles.kitWrapper}>
      <div className={styles.buildKitBanner}>
        <p>
          Create {indefinite(waterbody.primary_name)} Kit <br />
          <span>
            Target {species.display_name} During {seasonGroup.display_name}
          </span>
        </p>
        <OmniaButton
          kind="primary"
          size={isDesktop ? 'lg' : 'md'}
          onPress={() => {
            if (!selectedTechniques.length) {
              setSelectedTechniques([techniques[0]]);
            }
            setIsKitOpen(!isKitOpen);
            WebAnalytics.lakePageClick('[products].(build_your_kit)', webAnalyticsLakeClickArgs);
          }}
        >
          Build Your Kit <DownOutlined size={18} />
        </OmniaButton>
      </div>

      {selectedTechniques.length > 0 && (
        <div className={styles.kit}>
          {isKitOpen && <h3 className={styles.kitHeading}>Your Kit</h3>}
          <KitInline
            fishingIntents={selectedTechniques.map((t, i) => ({
              technique: t,
              species,
              waterbody,
              season_group: seasonGroup,
              state: null,
              kit_products: [],
              id: i,
            }))}
            fishingReports={fishingReports}
            showFooterLakeDetails={false}
            showFooterCloseButton={true}
            onFooterCloseClick={() => setIsKitOpen(false)}
            visible={isKitOpen}
            showTopWaterbody={false}
          />

          {isKitOpen && selectedTechniques.length < 3 && (
            <OmniaButton kind="tertiary" size="md" className={styles.dropdownButton}>
              <Dropdown
                menu={{
                  items: unselectedTechniques.map((t) => ({
                    label: `${t.display_name} ${Math.round(
                      (t.reportCount / fishingReports.length) * 100
                    )}%`,
                    key: t.name,
                  })),
                  onClick: ({ key }) => {
                    setSelectedTechniques([
                      ...selectedTechniques,
                      techniques.find((t) => t.name === key),
                    ]);
                  },
                }}
                trigger={['click']}
              >
                <span className={styles.dropdownButtonText}>
                  Add Technique <PlusOutlined />
                </span>
              </Dropdown>
            </OmniaButton>
          )}
        </div>
      )}
    </div>
  );
};
